import React, { useState, useEffect } from 'react';
import { fetchInitiatives, createInitiative } from '../../utils/api';
import { Button } from '../ui';

const UNIT_MANAGERS = [
  { value: 'user-001', label: '4 West - Sarah Chen', unit: '4 West', manager: 'Sarah Chen' },
  { value: 'user-002', label: '5 North - Jennifer Martinez', unit: '5 North', manager: 'Jennifer Martinez' },
  { value: 'user-003', label: '3 West - David Chen', unit: '3 West', manager: 'David Chen' },
  { value: 'user-004', label: '11 East - Michael Brown', unit: '11 East', manager: 'Michael Brown' }
];

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
];

const PRIORITY_BADGE = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  low: 'bg-gray-100 text-gray-800 border-gray-300'
};

const STATUS_BADGE = {
  pending: 'bg-gray-100 text-gray-800',
  'in-progress': 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800'
};

function InitiativesAssignmentsTab() {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('user-001');
  const [priority, setPriority] = useState('high');
  const [targetDate, setTargetDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchInitiatives();
      setInitiatives(data);
    } catch (err) {
      console.error('Failed to load initiatives:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenModal = () => {
    setName('');
    setDescription('');
    setAssignedTo('user-001');
    setPriority('high');
    setTargetDate('');
    setShowModal(true);
  };

  const handleSave = async () => {
    const selected = UNIT_MANAGERS.find(u => u.value === assignedTo);
    try {
      await createInitiative({
        name: name.trim(),
        description: description.trim(),
        assignedTo,
        assignedUnit: selected?.unit || '',
        assignedManager: selected?.manager || '',
        priority,
        targetDate: targetDate || undefined
      });
      setShowModal(false);
      await load();
    } catch (err) {
      console.error('Failed to create initiative:', err);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Initiatives</h1>
          <Button
            variant="default"
            onClick={handleOpenModal}
          >
            Create Initiative
          </Button>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded" />
            ))}
          </div>
        ) : initiatives.length === 0 ? (
          <p className="text-gray-600 py-8 text-center">No initiatives yet. Click &quot;Create Initiative&quot; to add one.</p>
        ) : (
          <div className="space-y-4">
            {initiatives.map((init) => (
              <div
                key={init.id}
                className="p-5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-50/80"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900">{init.name}</h3>
                    {init.description && (
                      <p className="text-sm text-gray-600 mt-1">{init.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-sm text-gray-500">
                        {init.assignedUnit} – {init.assignedManager}
                      </span>
                      <span className={`px-2 py-1 text-xs font-bold rounded border ${PRIORITY_BADGE[init.priority] || PRIORITY_BADGE.medium}`}>
                        {init.priority?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-bold rounded ${STATUS_BADGE[init.status] || STATUS_BADGE.pending}`}>
                        {init.status?.replace('-', ' ').toUpperCase()}
                      </span>
                      {init.targetDate && (
                        <span className="text-sm text-gray-500">
                          Target: {new Date(init.targetDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Initiative</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initiative Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Reduce ED Wait Times"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the initiative"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {UNIT_MANAGERS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
                <input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                variant="default"
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InitiativesAssignmentsTab;
